// src/components/stories/StorySlide.tsx
//
// Renders a single story slide: background, media (cover photo / contained logo /
// composed multi-image), progressively-revealed text blocks, and an optional CTA.

import { ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StorySlide as Slide, TextStyle } from '@/data/stories/types';
import { AnimatedText } from './AnimatedText';

const STYLE_CLASS: Record<TextStyle, string> = {
  headline: 'text-2xl sm:text-3xl font-bold leading-tight text-white drop-shadow',
  subhead: 'text-base sm:text-lg font-semibold text-white/90',
  body: 'text-sm sm:text-base leading-relaxed text-white/85',
  caption: 'text-xs font-bold uppercase tracking-[0.2em] text-primary',
};

interface StorySlideProps {
  slide: Slide;
  isRTL: boolean;
  paused: boolean;
  onCta: (url: string) => void;
}

export function StorySlide({ slide, isRTL, paused, onCta }: StorySlideProps) {
  const lang = isRTL ? 'ar' : 'en';
  const images = slide.media.filter((m) => m.type === 'image');
  const cover = images.find((m) => m.fit !== 'contain');
  const logos = images.filter((m) => m.fit === 'contain');
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{ background: slide.background ?? '#070138' }}
    >
      {/* Cover media */}
      {images.length > 1 && cover === undefined ? (
        // composed multi-logo / multi-image grid
        <div className="absolute inset-0 grid grid-cols-2 gap-1 p-1">
          {images.slice(0, 4).map((m, i) => (
            <img
              key={i}
              src={m.url}
              alt={m.alt?.[lang] ?? ''}
              loading="lazy"
              className={cn('h-full w-full', m.fit === 'contain' ? 'object-contain' : 'object-cover')}
            />
          ))}
        </div>
      ) : cover ? (
        <img
          src={cover.url}
          alt={cover.alt?.[lang] ?? ''}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : null}

      {/* Contained logo (partner slides) */}
      {logos.length === 1 && !cover && (
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <img
            src={logos[0].url}
            alt={logos[0].alt?.[lang] ?? ''}
            className="max-h-40 max-w-[70%] object-contain drop-shadow-2xl"
          />
        </div>
      )}

      {/* Readability scrim */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/40" />

      {/* Text + CTA */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 pb-8',
          isRTL ? 'items-end text-right' : 'items-start text-left',
        )}
      >
        {slide.textBlocks.map((block) => (
          <div key={block.id} className={STYLE_CLASS[block.style]}>
            <AnimatedText
              text={block.content[lang]}
              animation={block.animation}
              paused={paused}
              lang={lang}
            />
          </div>
        ))}

        {slide.cta && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCta(slide.cta!.url);
            }}
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-lg transition-transform hover:scale-105 active:scale-95"
          >
            {slide.cta.label[lang]}
            <Arrow className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
